import React, { FC, useCallback, useEffect, useState } from 'react'
import { Dialog } from 'primereact/dialog'
import { useNavigate } from 'react-router-dom'
import { useToast } from '../../../../common/toast/hooks/useToast'
import { TeamMember, TeamMemberFormProps } from './TeamMember.d'
import { Card } from 'primereact/card'
import useAxiosPrivate from '../../../../../hooks/useAxiosPrivate'
import Common from '../../../../common/Common'
import { Button, Alert, Row, Col } from 'react-bootstrap'
import { useAppSelector } from '../../../../../store/hooks'
import PageHeader from '../../../../layout/page/PageHeader'
import { Language } from '../../../../../features/security/security.slice'
import { Formik, Form as FormikForm } from 'formik'
import * as Yup from 'yup'
import TextField from '../../../../common/forms/fields/TextField'
import ImageField from '../../../../common/forms/fields/ImageField'
import DeleteDialog from '../../../../common/dialogs/DeleteDialog'
import RecordStatusField from '../../../../common/forms/fields/RecordStatusField'
import SelectField from '../../../../common/forms/fields/SelectField'
import RenderFieldsErrors from '../../../../common/forms/errors/RenderFieldsErrors'
import { handleAxiosErrors } from '../../../../common/forms/errors/Helper'
import RenderTranslatedFields from '../../../../common/forms/fields/translated-fields/RenderTranslatedFields'
import DateField from '../../../../common/forms/fields/DateField'
import FormActions from '../../../../common/forms/FormActions'
import {
    getGroupTranslation,
    getTranslation,
} from '../../../../common/utility/helpers'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons'
import RenderFormikErrors from '../../../../common/forms/errors/RenderFormikErrors'
import { HintMessage } from '../../../../common/messages/Messages'
import ImageReccomendedDimentionsMessage from '../../../../common/messages/ImageReccomendedDimentionsMessage'

const TeamMemberForm: FC<TeamMemberFormProps> = ({
    id = null,
    inPopup = true,
    config = {},
}) => {
    const isViewOrEdit = id !== null
    const pageCode = isViewOrEdit ? 'TEAM_MEMBER_' + id : 'TMPSET'
    const theme = useAppSelector((state) => state.theme.theme)
    const initData = useAppSelector((state: any) => state.security.initData)

    const navigate = useNavigate()
    const axiosPrivate = useAxiosPrivate()

    const emptyTeamMember: TeamMember = {
        titles_id: null,
        first_name: '',
        middle_names: '',
        last_name: '',
        date_of_birth: '',
        position: '',
        short_description: '',
        media_store_id: null,
        team_member_image: '',
        remove_team_member_image: false,
        order: null,
        departments_id: null,
        show_on_web: false,
    }

    const [teamMemberDialog, setTeamMemberDialog] = useState(true)
    const [teamMember, setTeamMember] = useState<TeamMember>(emptyTeamMember)
    const [recordName, setRecordName] = useState('')
    const [titles, setTitles] = useState<[]>([])
    const [departments, setDepartments] = useState<[]>([])
    const [availableSocialMedia, setAvailableSocialMedia] = useState<[]>([])
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isLoading, setIsLoading] = useState(true)
    const [allowRemoveImage, setAllowRemoveImage] = useState(false)
    const [previewSrc, setPreviewSrc] = useState('')
    const [errMsg, setErrMsg] = useState('')
    const [errorFields, setErrorFields] = useState<any>({})

    let schema = {
        first_name: Yup.string().trim().required('Required'),
        last_name: Yup.string().trim().required('Required'),
        position: Yup.string().trim().required('Required'),
        short_description: Yup.string().trim().required('Required'),
        departments_id: Yup.string().trim().required('Select a department'),
        order: Yup.string().trim().required('Enter an order number'),
    }

    let _initialValues = {
        ...teamMember,
        first_name: pageCode + '_FIRST_NAME',
        middle_names: pageCode + '_MIDDLE_NAMES',
        last_name: pageCode + '_LAST_NAME',
        position: pageCode + '_POSITION',
        short_description: pageCode + '_SHORT_DESCRIPTION',
        description: pageCode + '_DESCRIPTION',
    }

    Object.values(initData.languages.data).map((lang: Language) => {
        schema[`first_name_${lang.id}`] = Yup.string()
            .trim()
            .required(`The First Name (${lang.name}) field is required`)
        schema[`last_name_${lang.id}`] = Yup.string()
            .trim()
            .required(`The Last Name (${lang.name}) field is required`)
        schema[`position_${lang.id}`] = Yup.string()
            .trim()
            .required(`The Position (${lang.name}) field is required`)
        schema[`short_description_${lang.id}`] = Yup.string()
            .trim()
            .required(`The Short Description (${lang.name}) field is required`)
        _initialValues[`first_name_${lang.id}`] = ''
        _initialValues[`last_name_${lang.id}`] = ''
        _initialValues[`position_${lang.id}`] = ''
        _initialValues[`short_description_${lang.id}`] = ''
        _initialValues[`description_${lang.id}`] = ''
        return true
    })

    const [initialValues, setInitialValues] = useState(_initialValues)

    const TeamMemberFormSchema = Yup.object().shape(schema)

    const { triggerToast } = useToast()

    const loadPage = useCallback(async () => {
        setErrMsg('')
        setErrorFields({})
        setIsLoading(true)

        const routePath = isViewOrEdit
            ? '/team-members/' + id
            : '/team-members/new'

        try {
            const response = await axiosPrivate.get(routePath).finally(() => {
                setIsLoading(false)
            })
            if (response.data?.success === true) {
                setDepartments(response.data.data.departments)
                setTitles(response.data.data.titles)
                setAvailableSocialMedia(
                    response.data.data.available_social_media
                )

                if (!isViewOrEdit) {
                    setTeamMember((prevState) => {
                        return {
                            ...prevState,
                            order:
                                parseInt(response.data.data.highest_order) + 1,
                        }
                    })
                }

                if (response.data.data.teamMember) {
                    const member = response.data.data.teamMember
                    const translations = response.data.data.translations || {}
                    let _member = { ...member }
                    for (const i in _member.social_media || {}) {
                        _member[
                            'social-media-' + _member.social_media[i]['code']
                        ] = _member.social_media[i]['url']
                    }
                    setTeamMember(_member)
                    setRecordName(
                        getTranslation(
                            translations,
                            member.first_name,
                            initData.languages.default.locale
                        )
                    )

                    const translationCodes = [
                        { key: 'first_name', code: member.first_name },
                        { key: 'middle_names', code: member.middle_names },
                        { key: 'last_name', code: member.last_name },
                        { key: 'position', code: member.position },
                        { key: 'short_description', code: member.short_description },
                        { key: 'description', code: member.description },
                    ]
                    const nextInitial: Record<string, unknown> = {
                        ...initialValues,
                        ..._member,
                    }
                    Object.keys(initData.languages.data).forEach((key) => {
                        const lang: Language = initData.languages.data[key] as Language
                        const locale = lang?.locale ?? key
                        const groupTranslations = getGroupTranslation(
                            translations,
                            translationCodes,
                            locale
                        )
                        nextInitial[`first_name_${lang.id}`] = groupTranslations['first_name']
                        nextInitial[`middle_names_${lang.id}`] = groupTranslations['middle_names']
                        nextInitial[`last_name_${lang.id}`] = groupTranslations['last_name']
                        nextInitial[`position_${lang.id}`] = groupTranslations['position']
                        nextInitial[`short_description_${lang.id}`] = groupTranslations['short_description']
                        nextInitial[`description_${lang.id}`] = groupTranslations['description']
                    })
                    setInitialValues(nextInitial as typeof initialValues)
                }

                if (isViewOrEdit) {
                    setAllowRemoveImage(
                        response.data.data.teamMember.media_store_id !== null
                    )
                    setPreviewSrc(
                        response.data.data.teamMember.media_store_id
                            ? `${process.env.REACT_APP_API_ROOT_URL}/media/download/${response.data.data.teamMember.media_store_id}`
                            : ''
                    )
                } else {
                    setPreviewSrc('')
                }
            } else {
                setErrMsg(response.data?.errors.join('<br />'))
            }
        } catch (err) {
            // TODO: Sort out error handling!
            if (!err?.response) {
                setErrMsg('No Server Response')
            } else if (err.response?.status === 404) {
                setErrMsg('Record was not found')
            } else {
                setErrMsg('Failed to load team member')
            }
        }
    }, [axiosPrivate, id, initData.languages, isViewOrEdit])

    useEffect(() => {
        loadPage()
    }, [loadPage])

    const saveRecord = async (record) => {
        setErrMsg('')
        setIsSubmitting(true)

        delete record['social_media']

        record.remove_team_member_image =
            !record.team_member_image &&
            record.media_store_id !== null &&
            !allowRemoveImage

        try {
            const routePath = isViewOrEdit
                ? '/team-members/' + id
                : '/team-members/create'
            const response = await axiosPrivate
                .post(routePath, record, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                })
                .finally(() => {
                    setIsSubmitting(false)
                })

            if (response.data?.success === true) {
                triggerToast({
                    severity: 'success',
                    summary: config?.submitSuccessMsg?.header || 'Successful',
                    detail:
                        config?.submitSuccessMsg?.message ||
                        'Processed successfully',
                })
                hideDialog()
            } else {
                setErrMsg(response.data?.errors.join('<br />'))
            }
        } catch (err) {
            handleAxiosErrors(err, setErrMsg, setErrorFields, config)
        }
    }

    const hideDialog = () => {
        setTeamMemberDialog(false)
        navigate('/people/team/members')
    }

    const formFooter = (
        <React.Fragment>
            <Button
                variant={theme === 'dark' ? 'dark' : 'light'}
                onClick={hideDialog}
            >
                <span className="small">Cancel</span>
            </Button>
            {isViewOrEdit && (
                <DeleteDialog
                    url={'/team-members/delete/' + id}
                    record={teamMember}
                    deletedCallback={() => hideDialog()}
                    btnLabel="Delete"
                    showIcon={false}
                    message={
                        <p className="mb-3 pb-3 border-bottom">
                            Are you sure you want to delete this team member?
                        </p>
                    }
                />
            )}
            <Button
                variant="success"
                disabled={isSubmitting || isLoading}
                onClick={() => {
                    const formSubmitGtn = document.getElementById(
                        'teamMember-form-submit-btn'
                    )
                    formSubmitGtn.click()
                }}
            >
                <span className="small">
                    {isSubmitting ? (
                        <>
                            <FontAwesomeIcon icon={faCircleNotch} spin={true} />{' '}
                            {config?.saveButton?.savingLabel || 'Processing...'}
                        </>
                    ) : (
                        config?.saveButton?.label || 'Process'
                    )}
                </span>
            </Button>
        </React.Fragment>
    )

    const FormContent = (
        <>
            {isLoading && <Common.Loader text="Loading..." />}
            {errMsg && (
                <Alert show={true} variant="danger">
                    <Alert.Heading className="d-inline-block me-2">
                        Error!
                    </Alert.Heading>
                    <RenderFieldsErrors
                        errorFields={errorFields}
                        errMsg={errMsg}
                    />
                </Alert>
            )}
            <Formik
                initialValues={initialValues}
                validationSchema={TeamMemberFormSchema}
                onSubmit={(values) => {
                    saveRecord(values)
                }}
                enableReinitialize={true}
            >
                {(formikProps) => {
                    return (
                        <FormikForm id="teamMember-form">
                            <RenderFormikErrors formikProps={formikProps} />
                            <Row>
                                <Col sm={12}>
                                    <RecordStatusField
                                        id="formTeamMemberShowOnWeb"
                                        name="show_on_web"
                                        onLabel="Hide from website"
                                        offLabel="Show on website"
                                        formikProps={formikProps}
                                    />
                                </Col>
                                <Col lg={5}>
                                    <fieldset>
                                        <legend>General</legend>
                                        <Row>
                                            <Col xl={12}>
                                                <Row>
                                                    <Col xl={6}>
                                                        <SelectField
                                                            id="formTeamMemberPersontitlesId"
                                                            name="titles_id"
                                                            label="Title"
                                                            formikProps={
                                                                formikProps
                                                            }
                                                            choices={titles}
                                                        />
                                                    </Col>
                                                    <Col xl={6}>
                                                        <DateField
                                                            id="formTeamMemberPersonDateOfBirth"
                                                            name="date_of_birth"
                                                            label="Date of birth"
                                                            placeholder="DD/MM/YYYY"
                                                            formikProps={
                                                                formikProps
                                                            }
                                                        />
                                                    </Col>
                                                </Row>
                                            </Col>
                                            <Col xl={12}>
                                                <RenderTranslatedFields
                                                    id="teamMember"
                                                    formikProps={formikProps}
                                                    fields={[
                                                        {
                                                            id: 'formTeamMemberFirstName',
                                                            type: 'text',
                                                            name: 'first_name',
                                                            label: 'First Name',
                                                            placeholder:
                                                                'Enter first name',
                                                            required: true,
                                                        },
                                                        {
                                                            id: 'formTeamMemberMiddleNames',
                                                            type: 'text',
                                                            name: 'middle_names',
                                                            label: 'Middle Name(s)',
                                                            placeholder:
                                                                'Enter middle name(s)',
                                                        },
                                                        {
                                                            id: 'formTeamMemberLastName',
                                                            type: 'text',
                                                            name: 'last_name',
                                                            label: 'Last Name',
                                                            placeholder:
                                                                'Enter last name',
                                                            required: true,
                                                        },
                                                        {
                                                            id: 'formTeamMemberPosition',
                                                            type: 'text',
                                                            name: 'position',
                                                            label: 'Position',
                                                            placeholder:
                                                                "Enter member's position",
                                                            required: true,
                                                        },
                                                        {
                                                            id: 'formTeamMemberShortDescription',
                                                            type: 'textarea',
                                                            name: 'short_description',
                                                            label: 'Short Description',
                                                            placeholder:
                                                                "Enter member's short description",
                                                            required: true,
                                                        },
                                                    ]}
                                                />
                                            </Col>

                                            <Col xl={12}>
                                                <Row>
                                                    <Col xl={6}>
                                                        <TextField
                                                            id="formTeamMemberOrder"
                                                            name="order"
                                                            label="Order on Website"
                                                            placeholder="Enter order"
                                                            type="number"
                                                            formikProps={
                                                                formikProps
                                                            }
                                                            required
                                                        />
                                                    </Col>
                                                    <Col xl={6}>
                                                        <SelectField
                                                            id="formTeamMemberDepartmentsId"
                                                            name="departments_id"
                                                            label="Department"
                                                            formikProps={
                                                                formikProps
                                                            }
                                                            choices={
                                                                departments
                                                            }
                                                            required
                                                        />
                                                    </Col>
                                                </Row>
                                            </Col>
                                        </Row>
                                    </fieldset>
                                </Col>
                                <Col lg={4}>
                                    <fieldset>
                                        <legend>Social Media</legend>
                                        <Row>
                                            <Col xl={12}>
                                                {availableSocialMedia.map(
                                                    (socialMediaItem: any) => {
                                                        return (
                                                            <TextField
                                                                key={
                                                                    socialMediaItem.id
                                                                }
                                                                id={`formTeamMember${socialMediaItem.code}Url`}
                                                                name={`social-media-${socialMediaItem.code}`}
                                                                label={`${socialMediaItem.name}`}
                                                                placeholder={`Enter ${socialMediaItem.name} Url`}
                                                                formikProps={
                                                                    formikProps
                                                                }
                                                            />
                                                        )
                                                    }
                                                )}
                                            </Col>
                                        </Row>
                                    </fieldset>
                                </Col>
                                <Col lg={3}>
                                    <fieldset>
                                        <legend>
                                            Image{' '}
                                            <HintMessage
                                                message={
                                                    <ImageReccomendedDimentionsMessage size="1024 x 1024 pixles" />
                                                }
                                            />
                                        </legend>
                                        <ImageField
                                            id="teamMemberImageFile"
                                            name="team_member_image"
                                            label="Image"
                                            formikProps={formikProps}
                                            imageId={teamMember.media_store_id}
                                            alt={`${teamMember.first_name} image`}
                                            allowRemoveImage={allowRemoveImage}
                                            setAllowRemoveImage={
                                                setAllowRemoveImage
                                            }
                                            previewSrc={previewSrc}
                                            setPreviewSrc={setPreviewSrc}
                                            updateRecord={setTeamMember}
                                        />
                                    </fieldset>
                                </Col>
                                <Col sm={12}>
                                    <fieldset>
                                        <legend>Full Description/Bio</legend>
                                        <RenderTranslatedFields
                                            id="teamMemberDescription"
                                            formikProps={formikProps}
                                            fields={[
                                                {
                                                    id: 'formTeamMemberDescription',
                                                    type: 'textarea',
                                                    name: 'description',
                                                    label: 'Description',
                                                    showLabel: false,
                                                    placeholder:
                                                        'Enter button label',
                                                    enableEditor: true,
                                                },
                                            ]}
                                        />
                                    </fieldset>
                                </Col>
                            </Row>
                            <input
                                type="submit"
                                id="teamMember-form-submit-btn"
                                className="d-none"
                            />
                        </FormikForm>
                    )
                }}
            </Formik>
        </>
    )

    return (
        <>
            {inPopup ? (
                <Card className="flex justify-content-center">
                    <Dialog
                        visible={teamMemberDialog}
                        breakpoints={{ '960px': '100vw', '640px': '100vw' }}
                        style={{ width: '75vw' }}
                        header={
                            isViewOrEdit
                                ? isLoading
                                    ? 'Loading...'
                                    : 'Edit team member'
                                : 'Create new team member'
                        }
                        modal
                        className="p-fluid"
                        footer={formFooter}
                        onHide={hideDialog}
                        maximizable
                        contentClassName={isLoading ? 'wrapped-content' : ''}
                        closeOnEscape={false}
                    >
                        {FormContent}
                    </Dialog>
                </Card>
            ) : (
                <>
                    <PageHeader
                        header={
                            isViewOrEdit
                                ? isLoading
                                    ? 'Loading...'
                                    : `Edit ${recordName}`
                                : 'Create new team member'
                        }
                        buttons={[
                            {
                                variant: theme === 'dark' ? 'dark' : 'light',
                                label: 'Back to Team Members',
                                icon: 'fa-solid fa-arrow-left',
                                url: '/people/team/members',
                            },
                        ]}
                    />
                    {FormContent}
                    <FormActions formFooter={formFooter} />
                </>
            )}
        </>
    )
}

export default TeamMemberForm
